import { useEffect, useLayoutEffect, useRef } from "react";
import { useContextAgent } from "./ContextAgentContext";

/**
 * Registers a scoped system agent with the app-level context rail (Option A).
 * Mount on builder pages; unregisters on unmount.
 */
export default function useRegisterContextAgent({
  enabled,
  agentName,
  scopeLabel,
  scopeId,
  initialOpen = false,
  panel,
  onToggleClick,
  controlledOpen,
  controlledOnOpenChange,
}) {
  const {
    setRegistration,
    clearRegistration,
    syncHandlers,
    setOpen,
    open,
  } = useContextAgent();
  const autoOpenedRef = useRef(false);
  const prevScopeIdRef = useRef(scopeId);

  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }
    syncHandlers({
      panel,
      onOpenChange: controlledOnOpenChange,
      onToggleClick,
    });
  });

  useLayoutEffect(() => {
    if (!enabled) {
      return undefined;
    }

    setRegistration({
      agentName,
      scopeLabel,
      scopeId,
      panel,
      onToggleClick,
      open: controlledOpen,
      onOpenChange: controlledOnOpenChange,
    });

    return () => clearRegistration();
  }, [
    enabled,
    agentName,
    scopeLabel,
    scopeId,
    controlledOpen,
    setRegistration,
    clearRegistration,
  ]);

  useLayoutEffect(() => {
    if (enabled) {
      return undefined;
    }
    clearRegistration();
    return undefined;
  }, [enabled, clearRegistration]);

  useEffect(() => {
    if (!enabled) {
      autoOpenedRef.current = false;
      return;
    }
    if (initialOpen && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      if (controlledOnOpenChange) {
        controlledOnOpenChange(true);
      } else {
        setOpen(true);
      }
    }
  }, [enabled, initialOpen, setOpen, controlledOnOpenChange]);

  useEffect(() => {
    if (!enabled) return;
    if (prevScopeIdRef.current !== scopeId && prevScopeIdRef.current !== undefined) {
      if (controlledOnOpenChange) {
        controlledOnOpenChange(false);
      } else {
        setOpen(false);
      }
      autoOpenedRef.current = false;
    }
    prevScopeIdRef.current = scopeId;
  }, [enabled, scopeId, setOpen, controlledOnOpenChange]);

  return { open, setOpen };
}
